import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useIsMobile } from 'WindowDimensionProvider';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
  ListingPictureKind,
} from '@kouto/types';
import { useTranslation } from 'react-i18next';
import MinimalVideoPlayer from 'components/MinimalVideoPlayer/MinimalVideoPlayer';
import { useInView } from 'react-intersection-observer';
import {
  getListingRoute,
  formatListingMedia,
  listingMediaIsVideo,
} from 'utils/listings';
import { analyticsManager } from 'features/analytics';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import ChipList from 'components/ChipList';
import Chip from 'components/Chip';
import BrandFlagContainer from 'components/molecules/BrandFlagContainer/BrandFlagContainer';
import { NormalizedPublicListing } from 'types/listingV3';
import useListingHref from 'hooks/useListingHref';
import { LAST_CLICKED_LISTING_SESSION_STORAGE_KEY } from 'consts';
import { ListingInfoBox } from './ListingInfoBox';
import ListingItemCardDetailsLabel from './ListingItemCardDetailsLabel';

const ZOOM_IN_DURATION = 3000; // ms

interface ListingItemCardProps {
  gridArea: string;
  listing: NormalizedPublicListing;
  showYear?: boolean;
  brandSettings?: { promotion?: { exclusiveTag?: string } };
}

const ListingItemCard = ({
  gridArea,
  listing,
  showYear,
  brandSettings,
}: ListingItemCardProps) => {
  const isMobile = useIsMobile();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const listingRoute = getListingRoute(listing);
  const listingHref = useListingHref(listingRoute);

  const displayableMedias = listing?.medias?.filter(
    (media) =>
      media.kind === ListingPictureKind.COVER ||
      media.kind === ListingPictureKind.GALLERY,
  );

  const mediaCount = displayableMedias?.length;

  const formattedMedias = formatListingMedia(displayableMedias).map(
    (media) => ({
      ...media,
      alt: media.altText || listing.title,
    }),
  );

  const stopInterval = () => {
    setIntervalId((prevIntervalId) => {
      if (prevIntervalId) {
        clearInterval(prevIntervalId);
      }
      return null;
    });
  };

  const startInterval = () => {
    setIntervalId(setInterval(goToNextMedia, ZOOM_IN_DURATION - 500));
  };

  const goToNextMedia = () => {
    setSelectedImageIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % formattedMedias.length;
      if (formattedMedias[nextIndex].type === 'video') {
        stopInterval();
      }
      return nextIndex;
    });
  };

  useEffect(() => stopInterval, []);

  const onVideoEnd = () => {
    goToNextMedia();
    startInterval();
  };

  const onMouseEnter = () => {
    setHovered(true);
    stopInterval();
    if (formattedMedias[selectedImageIndex].type !== 'video') {
      startInterval();
    }
  };

  const onMouseLeave = () => {
    stopInterval();
    setHovered(false);
    setSelectedImageIndex(0);
  };

  const handleNavigation = () => {
    stopInterval();
    sessionStorage.setItem(
      LAST_CLICKED_LISTING_SESSION_STORAGE_KEY,
      listing.id,
    );
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
      [ANALYTICS_PROPERTY.ExperienceId]: listing.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing.title ?? undefined,
      [ANALYTICS_PROPERTY.ExperienceCategory]:
        listing.categoryName ?? undefined,
      [ANALYTICS_PROPERTY.Products]: JSON.stringify([
        {
          category: listing.categoryName,
          id: listing.id,
          index: 0,
          itemListName: 'Browse',
          startingPrice: listing.startingPrice,
          title: listing.title,
        },
      ]),
    });
    history.push(listingRoute);
  };

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const isNewTabIntent = e.metaKey || e.ctrlKey || e.button === 1;

    if (isNewTabIntent) {
      stopInterval();
      sessionStorage.setItem(
        LAST_CLICKED_LISTING_SESSION_STORAGE_KEY,
        listing.id,
      );
      analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
        [ANALYTICS_PROPERTY.ExperienceId]: listing.id,
        [ANALYTICS_PROPERTY.ExperienceTitle]: listing.title ?? undefined,
        [ANALYTICS_PROPERTY.ExperienceCategory]:
          listing.categoryName ?? undefined,
        [ANALYTICS_PROPERTY.Products]: JSON.stringify([
          {
            id: listing.id,
            title: listing.title,
            category: listing.categoryName,
            startingPrice: listing.startingPrice,
            index: 0,
            // Attribute the booking to the browse list in GA4 (item_list_name on select_item).
            itemListName: 'Browse',
          },
        ]),
      });
      // Don't prevent default - let browser open new tab
      return;
    }

    // Normal click - prevent default and use history.push
    e.preventDefault();
    handleNavigation();
  };

  const thereIsOnlyMediasOfTypeVideo = formattedMedias.every((media) =>
    listingMediaIsVideo(media),
  );

  const [mediaLoading, setMediaLoading] = useState(true);
  const { ref, inView } = useInView({ triggerOnce: true });

  const isLoading = (mediaLoading && !thereIsOnlyMediasOfTypeVideo) || !inView;

  const vibesEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_VIBES_V2,
  );

  return (
    <Wrapper
      ref={ref}
      as="a"
      gridArea={gridArea}
      className="listing-item-card-container"
      data-listing-id={listing.id}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      href={listingHref}
      onClick={onClick}
      onAuxClick={onClick}
    >
      <ListingInfoBox
        dates={listing.dates}
        hostData={listing.hostedBy}
        productLine={listing.productLine}
        providerBrandName={listing.promotedListing?.providerBrandName}
      />
      <MediaWrapper
        className={isLoading ? 'loading' : ''}
        mediaCount={mediaCount ?? 0}
      >
        <BrandFlagContainer
          brandFlagName={
            listing.pointsEligible?.payEligible
              ? listing.pointsEligible?.org
              : undefined
          }
        >
          {listing.isExclusive && (
            <ExclusiveBadge className="listing-card-exclusive-badge">
              <span>
                {brandSettings?.promotion?.exclusiveTag ||
                  translate('exclusive')}
              </span>
            </ExclusiveBadge>
          )}
          {formattedMedias.map((media, index) =>
            listingMediaIsVideo(media) ? (
              <MinimalVideoPlayer
                key={media.id}
                videoUrl={isMobile ? media.urlMobile : media.urlDesktop}
                coverUrl={isMobile ? media.previewMobile : media.previewDesktop}
                loop={formattedMedias.length === 1}
                play={hovered && selectedImageIndex === index}
                onVideoEnd={onVideoEnd}
                onVideoLoaded={() => {
                  setMediaLoading(false);
                }}
                className="listing-card-background-video"
                customCss={getVideoCss(
                  selectedImageIndex === index,
                  mediaCount ?? 0,
                  index,
                )}
              />
            ) : (
              <Img
                key={media.id}
                id={media.id}
                onLoad={() => {
                  setMediaLoading(false);
                }}
                loading="lazy"
                className="listing-card-background-image"
                src={isMobile ? media.urlMobile : media.urlDesktop}
                hovered={hovered}
                active={selectedImageIndex === index}
                mediaCount={mediaCount ?? 0}
                mediaIndex={index}
                alt={media.alt ?? undefined}
              />
            ),
          )}
        </BrandFlagContainer>
      </MediaWrapper>
      <Title className="listing-card-title">{listing.title}</Title>
      {vibesEnabled && listing.vibes && listing.vibes.length > 0 && (
        <StyledChipList aria-label="Vibes" className="listing-card-vibes">
          {listing.vibes.map(({ name }) => (
            <Chip as="li" key={name} className="listing-card-vibe">
              {name}
            </Chip>
          ))}
        </StyledChipList>
      )}
      <ListingItemCardDetailsLabel
        advertisedPrice={listing.advertisedPrice}
        bookingAvailabilityMode={listing.bookingAvailabilityMode}
        dates={listing.dates}
        hidePrice={listing.hidePrice}
        maxParticipants={listing.maxParticipants}
        productLine={listing.productLine}
        showYear={showYear}
        startingPrice={listing.startingPrice}
      />
    </Wrapper>
  );
};

export default ListingItemCard;

const Wrapper = styled.div<{ gridArea: string }>`
  grid-area: ${(props) => props.gridArea};
  cursor: pointer;
  position: relative;
  /* Comprehensive text-decoration reset */
  text-decoration: none !important;
  text-decoration-line: none !important;
  text-decoration-style: none !important;
  text-decoration-color: initial !important;

  /* Reset for all children including links */
  & *,
  & a,
  & a:hover,
  & a:focus,
  & a:visited,
  & a:active {
    text-decoration: none !important;
  }
`;

const MediaWrapper = styled.div<{ mediaCount: number }>`
  width: 100%;
  height: 380px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 300px;
  }

  &:hover img.listing-card-background-image {
    scale: 1.05;
  }

  & > img.listing-card-background-image,
  & > img.listing-card-background-image[loading='lazy'] {
    /* this css is defined here instead of inside <Img> component to get an higher priority than the css on the Turtle Bay website */
    height: 100%;
    opacity: 1;
    transition: scale ${ZOOM_IN_DURATION / 1000}s cubic-bezier(0, 0.7, 0.28, 1);
  }

  &::before {
    content: ' ';
    opacity: 0;
    transition: opacity 0.4s 0.2s ease-in-out;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(-90deg, #c1c1c1 0%, #f8f8f8 50%, #c1c1c1 100%);
    background-size: 400% 400%;
    z-index: 0;
  }

  &.loading::before {
    opacity: 1;
    animation: pulse 1.2s ease-in-out infinite;
    z-index: ${({ mediaCount }) => 30 + mediaCount + 1};

    @keyframes pulse {
      0% {
        background-position: 0% 0%;
      }
      100% {
        background-position: -135% 0%;
      }
    }
  }
`;

export const Img = styled.img<{
  hovered: boolean;
  active: boolean;
  mediaCount: number;
  mediaIndex: number;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  scale: 1;
  z-index: ${({ mediaCount, mediaIndex }) => mediaCount - mediaIndex};
  animation-iteration-count: 1;
  transition: scale ${ZOOM_IN_DURATION / 1000}s cubic-bezier(0, 0.7, 0.28, 1);

  ${({ active, mediaIndex }) => active && `z-index: ${30 + mediaIndex};`}
`;

const getVideoCss = (
  active: boolean,
  mediaCount: number,
  mediaIndex: number,
) => `
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: ${active ? 30 + mediaIndex : mediaCount - mediaIndex};

  ${
    !active
      ? `
    & button {
      display: none;
    }
  `
      : ''
  }
`;

/* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
const Title = styled.h3`
  font-size: 20px;
  font-weight: 400;
  margin-top: 16px;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--way-colors-primaryTextColor);
`;

const StyledChipList = styled(ChipList)`
  margin-top: 10px;
`;

const ExclusiveBadge = styled.div`
  position: absolute;
  display: flex;
  padding: 12px;
  align-items: end;
  justify-content: end;
  right: 0px;
  bottom: 0px;
  width: 100%;
  height: 100%;
  z-index: 99;
  background: linear-gradient(
    143deg,
    rgba(0, 0, 0, 0) 80%,
    var(--way-palette-black-50) 100%
  );

  & > span {
    color: var(--way-palette-white-100);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`;
